import { type LucideIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export interface MetricItem {
  label: string;
  value: number;
  icon: LucideIcon;
  color?: string;
  /** Shown beside the primary number (e.g. "retryable"). */
  valueCaption?: string;
  /** Second statistic in the same card (e.g. locked failures). */
  secondary?: { value: number; label: string };
}

interface MetricCardsProps {
  metrics: MetricItem[];
  loading?: boolean;
}

export function MetricCards({ metrics, loading }: MetricCardsProps) {
  const n = Math.max(metrics.length, 1);
  const colsClass =
    n >= 6
      ? "md:grid-cols-6"
      : n >= 5
        ? "md:grid-cols-5"
        : n === 4
          ? "md:grid-cols-4"
          : n === 3
            ? "md:grid-cols-3"
            : "md:grid-cols-2";
  if (loading) {
    return (
      <div className={`grid gap-4 ${colsClass}`}>
        {Array.from({ length: Math.max(metrics.length, 6) }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-10 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={`grid gap-4 ${colsClass}`}>
      {metrics.map((m) => (
        <Card key={m.label}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {m.label}
            </CardTitle>
            <m.icon className={`h-4 w-4 ${m.color || "text-muted-foreground"}`} />
          </CardHeader>
          <CardContent className="space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
              <span className="text-2xl font-bold tabular-nums">
                {m.value.toLocaleString()}
              </span>
              {m.valueCaption ? (
                <span className="text-xs font-medium text-muted-foreground">
                  {m.valueCaption}
                </span>
              ) : null}
            </div>
            {m.secondary ? (
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                <span className="text-xl font-semibold tabular-nums text-muted-foreground">
                  {m.secondary.value.toLocaleString()}
                </span>
                <span className="text-xs text-muted-foreground">{m.secondary.label}</span>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
